import { auth } from "../../firebase/config"; // Adjust path as needed
import { jwtDecode } from "jwt-decode"; // Ensure this is installed and imported

let refreshingToken = null; // Global lock to prevent multiple refresh calls

// Helper function to check if the token is expired
function isTokenExpired(token) {
  try {
    if (typeof token !== "string") return false;
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000; // Current time in seconds
    const earlyExpiryTime = decodedToken.exp - 600; // Refresh 3300 seconds (55 min) before expiry
    return earlyExpiryTime < currentTime;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true; // Assume expired if decoding fails
  }
}

// Helper function to refresh the token
async function refreshToken() {
  try {
    if (!auth?.currentUser) {
      console.warn("refreshToken: User is not authenticated.");
      throw new Error("User not authenticated");
    }
    const newToken = await auth.currentUser.getIdToken(true); // Force refresh
    localStorage.setItem("token", newToken);
    return newToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error; // Propagate error to handle upstream
  }
}

// Updated fetchSecure with token refresh and retry
export async function fetchSecure(url, options) {
  let token = localStorage.getItem("token");
  const wixToken = localStorage.getItem("wixToken");

  // Check if token exists and is expired
  if (token && isTokenExpired(token)) {
    try {
      if (!refreshingToken) {
        refreshingToken = refreshToken(); // Start refresh
      }
      const newToken = await refreshingToken; // Wait for refresh
      refreshingToken = null; // Reset lock

      if (!newToken) {
        throw new Error("Failed to refresh token");
      }

      localStorage.setItem("token", newToken); // Update storage
      token = newToken; // Use the new token directly
    } catch (error) {
      refreshingToken = null; // Reset lock on failure
      throw new Error("Token refresh failed: " + error.message); // Propagate error
    }
  }

  if (options === undefined) {
    options = {
      headers: {
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      },
    };
  } else {
    if (!("headers" in options)) {
      options["headers"] = {};
      options.headers = {
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      };
    } else {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        Wixauthorization: `Bearer ${wixToken}`,
      };
    }
  }
  //console.log(options);
  return fetch(url, options);
  // let newurl = `http://localhost:3000${url}`;
  // return fetch(newurl, options);
}

export function evaluateDynamicLogic(logicString, variables) {
  const regex =
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|\w+(?:\.\w+)*)\s*(==|!=|IN|in|NOT_IN|not_in)\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|\w+(?:\.\w+)*|\d+)/g; // Corrected Regex

  const matches = logicString.matchAll(regex);

  let result = true;

  for (const match of matches) {
    const [_, variable1, operator, variable2] = match;

    // Improved nested property access for variable1 (same as before)
    /* ORIGINAL April 21, 2025  DELETE EVENTUALLY KAD
    let value1 = variables;
       const variable1Parts = variable1.split(".");
       for (const part of variable1Parts) {
         if (value1 && typeof value1 === "object" && part in value1) {
           value1 = value1[part];
         } else {
           value1 = undefined;
           break;
         }
       }
    }
*/
    let value1 =
      variable1; /* KAD changed variables to variable1 April 21, 2025 */
    const variable1Parts = variable1.split(".");
    if (variable1.includes(".")) {
      for (const part of variable1Parts) {
        if (value1 && typeof value1 === "object" && part in value1) {
          value1 = value1[part];
        } else if (
          /* Vik(May 12, 2025): Setting userId from the users object. */
          value1 &&
          typeof variables === "object" &&
          operator.toLowerCase() === "in"
        ) {
          value1 = variables[part];
        } else {
          value1 = undefined;
          break;
        }
      }
    }

    const value2 = variables[variable2] || variable2; // Handle literal values
    //console.log(value1 + "," + JSON.stringify(value2) + "," + variable2);

    if (operator.toLowerCase() === "in") {
      //result = result && Array.isArray(value2) && value2?.includes(value1);
      /* KAD relaxed this Array ..so I can do an this on a string */
      result =
        result &&
        (Array.isArray(value2) || typeof value2 === "string") &&
        value2?.includes(value1);
      // console.log("IN HERE"  + result);
    } else if (operator.toLowerCase() === "not_in") {
      //result = result && Array.isArray(value2) && value2?.includes(value1);
      /* KAD relaxed this Array ..so I can do an this on a string */
      result =
        result &&
        (Array.isArray(value2) || typeof value2 === "string") &&
        !value2?.includes(value1);
      //console.log("IN HERE NOT IN "  + result);
    } else if (operator === "==") {
      result = result && value1 === value2;
    } else if (operator === "!=") {
      result = result && value1 !== value2;
    } else {
      console.error("Invalid operator:", operator);
      console.error("Logic string:", logicString);
      return false;
    }
  }

  return result;
}
export function evaluateCondition(row, formData) {
  if (typeof row.conditional === "undefined") {
    return true; // No condition, so it's always true
  }

  const condition = row.conditional.trim(); // Remove leading/trailing spaces
  const parts = condition.split(/(&&|\|\|)/); // Split by && or ||

  let result = true; // Default to true for the first condition

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();

    if (part === "&&" || part === "||") {
      const operator = part;
      const nextPart = parts[i + 1].trim();
      i++; // Skip the next part, as we've already processed it

      const nextValues = nextPart.split(" ");
      const nextConditionResult =
        formData.data[nextValues[0]] === nextValues[2];

      if (operator === "&&") {
        result = result && nextConditionResult;
      } else {
        result = result || nextConditionResult;
      }
    } else {
      const values = part.split(" ");
      result = formData.data[values[0]] === values[2];
    }
  }

  return result;
}
export const capitalize = (field) => {
  return field[0].toUpperCase() + field.substring(1);
};

export function getUniqueObjects(arr, key) {
  const seen = new Set(); // Use a Set to efficiently track unique values

  return arr.filter((obj) => {
    const value = obj[key]; // Get the value of the specified key

    if (value !== undefined && !seen.has(value)) {
      seen.add(value); // Add the value to the Set if it's not already there
      return true; // Keep the object in the filtered array
    }
    return false; // Discard the object if the value has already been seen
  });
}
